import { Check, Palette } from '@/components/ui/icons';
import { useMemo } from 'react';
import { ThemeSwatch } from '@/components/settings/ThemeSwatch';
import { Button } from '@/components/ui/Button';
import { saveSettings } from '@/lib/settings';
import { decodeThemeCode, encodeThemeCode } from '@/lib/themes';
import { useAuth } from '@/stores/auth';
import { confirm, toast } from '@/stores/ui';

/** A theme code in a message, shown as the theme itself with a button to use it. */
export function ThemeEmbed({ code }: { code: string }) {
  const theme = useMemo(() => decodeThemeCode(code), [code]);
  const settings = useAuth((s) => s.settings);
  if (!theme || !settings) return null;
  const inUse = settings.theme === 'CUSTOM' && !!settings.customTheme && encodeThemeCode(settings.customTheme) === encodeThemeCode(theme);

  const use = async () => {
    // Someone else's colours replace your own custom theme, so check first.
    if (settings.customTheme && encodeThemeCode(settings.customTheme) !== encodeThemeCode(theme)) {
      const ok = await confirm({
        title: 'Use this theme?',
        body: 'It replaces your custom theme. You can copy your current one from Settings → Appearance first if you want to keep it.',
        confirmLabel: 'Use theme',
      });
      if (!ok) return;
    }
    if (await saveSettings({ theme: 'CUSTOM', customTheme: theme })) toast.success('Theme applied. Change it any time in Settings → Appearance.');
  };

  return (
    <div className="mt-1.5 w-full max-w-sm overflow-hidden rounded-2xl border border-line bg-elevated p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold tracking-wide text-fg-muted uppercase">
        <Palette size={14} /> ClanFire theme · {theme.base === 'DARK' ? 'Dark' : 'Light'}
      </p>
      <ThemeSwatch theme={theme} detailed className="h-24" />
      <div className="mt-2.5 flex items-center justify-end">
        {inUse ? (
          <span className="flex items-center gap-1.5 text-sm font-semibold text-success">
            <Check size={16} /> In use
          </span>
        ) : (
          <Button size="sm" onClick={() => void use()}>
            Use this theme
          </Button>
        )}
      </div>
    </div>
  );
}
